import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { FaPaperPlane } from 'react-icons/fa';
import emailjs from '@emailjs/browser';

const ContactForm = () => {
  const form = useRef();
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: '',
    loading: false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus(prev => ({ ...prev, loading: true, submitted: false, success: false }));
    
    emailjs.sendForm(
      'service_56xt3se',    // Replace with your EmailJS service ID
      'template_8coufx9',  // Replace with your EmailJS template ID
      form.current,
      '75CfkVohdHy77_ql3'    // Replace with your EmailJS public key
    )
      .then((result) => {
        setFormStatus({
          submitted: true,
          success: true,
          message: 'Thank you for your message! We will get back to you soon.',
          loading: false
        });
        form.current.reset();
      }, (error) => {
        setFormStatus({
          submitted: true,
          success: false,
          message: 'There was an error sending your message. Please try again.',
          loading: false
        });
      });
  };

  return (
    <FormContainer>
      <FormSubtitle>
        Have a question or want to book our services? Send us a message and we'll get back to you as soon as possible.
      </FormSubtitle>
      
      {formStatus.submitted ? (
        <FormMessage success={formStatus.success}>
          {formStatus.message}
        </FormMessage>
      ) : (
        <Form ref={form} onSubmit={handleSubmit}>
          <FormGroup>
            <FormLabel htmlFor="name">Name</FormLabel>
            <FormInput
              type="text"
              id="name"
              name="user_name"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <FormLabel htmlFor="email">Email</FormLabel>
            <FormInput
              type="email"
              id="email"
              name="user_email"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <FormLabel htmlFor="phone">Phone</FormLabel>
            <FormInput
              type="tel"
              id="phone"
              name="user_phone"
            />
          </FormGroup>
          
          <FormGroup>
            <FormLabel htmlFor="service">Service Interested In</FormLabel>
            <FormSelect
              id="service"
              name="service_type"
              required
            >
              <option value="">Select a Service</option>
              <option value="dj-wedding">DJ Services - Wedding</option>
              <option value="dj-corporate">DJ Services - Corporate Event</option>
              <option value="dj-nightclub">DJ Services - Nightclub</option>
              <option value="dj-private">DJ Services - Private Party</option>
              <option value="audio-engineering">Audio Engineering</option>
              <option value="audio-installation">Audio Installation</option>
              <option value="lighting-production">Lighting Production</option>
              <option value="equipment-repair">Equipment Repair</option>
              <option value="vinyl-sales">Vinyl & Equipment Sales</option>
              <option value="other">Other</option>
            </FormSelect>
          </FormGroup>
          
          <FormGroup>
            <FormLabel htmlFor="message">Message</FormLabel>
            <FormTextarea
              id="message"
              name="message"
              rows="5"
              required
            ></FormTextarea>
          </FormGroup>
          
          <SubmitButton type="submit" disabled={formStatus.loading}>
            <FaPaperPlane />
            <span>{formStatus.loading ? 'Sending...' : 'Send Message'}</span>
          </SubmitButton>
        </Form>
      )}
    </FormContainer>
  );
};

const FormContainer = styled.div`
  background-color: rgba(0, 0, 0, 0.3);
  padding: 2.5rem;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const FormSubtitle = styled.p`
  text-align: center;
  margin-bottom: 2rem;
  opacity: 0.8;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const FormLabel = styled.label`
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--color-tertiary);
  text-align: left;
`;

const FormInput = styled.input`
  padding: 0.8rem 1rem;
  border: 1px solid rgba(224, 221, 209, 0.2);
  border-radius: 4px;
  background-color: rgba(19, 19, 19, 0.8);
  color: var(--color-tertiary);
  font-family: var(--font-primary);
  transition: var(--transition);
  
  &:focus {
    outline: none;
    border-color: var(--color-secondary);
    box-shadow: 0 0 0 2px rgba(193, 195, 143, 0.2);
  }
`;

const FormSelect = styled.select`
  padding: 0.8rem 1rem;
  border: 1px solid rgba(224, 221, 209, 0.2);
  border-radius: 4px;
  background-color: rgba(19, 19, 19, 0.8);
  color: var(--color-tertiary);
  font-family: var(--font-primary);
  transition: var(--transition);
  width: 100%;
  min-width: 100%;
  height: 3.5rem;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23c1c38f' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1em;
  
  &:focus {
    outline: none;
    border-color: var(--color-secondary);
    box-shadow: 0 0 0 2px rgba(193, 195, 143, 0.2);
  }

  @media (max-width: 768px) {
    height: 4rem;
    padding: 0 1rem;
    font-size: 16px; /* Prevents zoom on iOS */
  }
`;

const FormTextarea = styled.textarea`
  padding: 0.8rem 1rem;
  border: 1px solid rgba(224, 221, 209, 0.2);
  border-radius: 4px;
  background-color: rgba(19, 19, 19, 0.8);
  color: var(--color-tertiary);
  font-family: var(--font-primary);
  resize: vertical;
  min-height: 120px;
  transition: var(--transition);
  
  &:focus {
    outline: none;
    border-color: var(--color-secondary);
    box-shadow: 0 0 0 2px rgba(193, 195, 143, 0.2);
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.8rem 2rem;
  background-color: var(--color-primary);
  color: var(--color-tertiary);
  border: none;
  border-radius: 4px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: var(--transition);
  cursor: pointer;
  
  &:hover {
    background-color: var(--color-secondary);
    color: var(--color-dark);
  }
  
  svg {
    font-size: 1rem;
  }
`;

const FormMessage = styled.div`
  padding: 1.5rem;
  text-align: center;
  background-color: ${({ success }) => 
    success ? 'rgba(65, 94, 78, 0.2)' : 'rgba(220, 53, 69, 0.2)'};
  border-radius: 4px;
  color: ${({ success }) => 
    success ? 'var(--color-secondary)' : '#dc3545'};
  font-weight: 500;
`;

export default ContactForm; 